import React, { useState } from "react";
import { Input, Button, Modal, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { addSubtopic } from "../../../services/polly/classificationService";

const HEALTH_COLORS = {
  green: '#3B6D11',
  amber: '#BA7517',
  red: '#E24B4C',
};

function getHealthColor(highConfidencePct) {
  if (highConfidencePct >= 80) return 'green';
  if (highConfidencePct >= 65) return 'amber';
  return 'red';
}

function matchesSearch(topic, search) {
  if (!search) return true;
  const s = search.toLowerCase();
  if ((topic.name || '').toLowerCase().includes(s)) return true;
  if (topic.children) return topic.children.some((c) => matchesSearch(c, s));
  return false;
}

function TopicNode({ topic, level, selectedTopicId, expandedIds, onSelect, onToggle, search }) {
  const hasChildren = topic.children && topic.children.length > 0;
  const isExpanded = expandedIds.has(topic.id);
  const isSelected = topic.id === selectedTopicId;
  const healthKey = getHealthColor(topic.highConfidencePct || 0);

  if (search && !matchesSearch(topic, search)) return null;

  return (
    <div className="ca-tree-node">
      <div
        className={`ca-tree-row${isSelected ? ' selected' : ''}${topic.disabled ? ' disabled' : ''}`}
        style={{ paddingLeft: 8 + level * 16 }}
        onClick={() => onSelect(topic.id)}
      >
        <span
          className="ca-tree-toggle"
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) onToggle(topic.id);
          }}
        >
          {hasChildren ? (
            <span className={`ca-tree-arrow${isExpanded ? ' open' : ''}`}>&#9654;</span>
          ) : (
            <span className="ca-tree-spacer" />
          )}
        </span>
        <span className="ca-tree-name">{topic.name}</span>
        <span className="ca-tree-count">
          {hasChildren && topic.ownConversationCount !== topic.conversationCount
            ? <><span className="ca-tree-count-own">{topic.ownConversationCount || 0}</span> / {topic.conversationCount || 0}</>
            : topic.conversationCount || 0
          }
        </span>
        <span className="ca-tree-dot" style={{ backgroundColor: HEALTH_COLORS[healthKey] }} />
      </div>
      {hasChildren && isExpanded && (
        <div className="ca-tree-children">
          {topic.children.map((child) => (
            <TopicNode
              key={child.id}
              topic={child}
              level={level + 1}
              selectedTopicId={selectedTopicId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggle={onToggle}
              search={search}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TopicTree({ topics, selectedTopicId, onSelect, onTopicUpdate }) {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAddRoot = async () => {
    const trimmed = newTopicName.trim();
    if (!trimmed) return;
    setAdding(true);
    try {
      await addSubtopic(trimmed, null);
      message.success("Root topic added");
      setAddModalVisible(false);
      setNewTopicName('');
      onTopicUpdate && onTopicUpdate();
    } catch (err) {
      message.error("Failed to add topic");
    }
    setAdding(false);
  };

  const handleToggle = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="ca-tree-pane">
      <div className="ca-tree-toolbar">
        <Input
          size="small"
          placeholder="Search topics..."
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          allowClear
          onChange={(e) => setSearch(e.target.value)}
          className="ca-tree-search"
        />
        <Button size="small" icon={<PlusOutlined />} onClick={() => { setNewTopicName(''); setAddModalVisible(true); }} className="ca-tree-add-btn">
          Add topic
        </Button>
      </div>
      <div className="ca-tree-body">
        {(topics || []).map((topic) => (
          <TopicNode
            key={topic.id}
            topic={topic}
            level={0}
            selectedTopicId={selectedTopicId}
            expandedIds={expandedIds}
            onSelect={onSelect}
            onToggle={handleToggle}
            search={search}
          />
        ))}
      </div>
      <Modal
        title="Add root topic"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        onOk={handleAddRoot}
        okText="Add"
        confirmLoading={adding}
        okButtonProps={{ disabled: !newTopicName.trim() }}
      >
        <Input
          placeholder="Topic name"
          value={newTopicName}
          onChange={(e) => setNewTopicName(e.target.value)}
          onPressEnter={handleAddRoot}
          autoFocus
        />
      </Modal>
    </div>
  );
}
